import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { getCachedAuthUser } from '../../utils/cachedAuth';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createRequestStatusObject } from '../utils';
import {
	BadgeDefinition,
	MetricRegistryEntry,
	UserBadge,
	UserBadgeProgress
} from '../../types/badges';

interface UserBadgesResponse {
	badges: UserBadge[];
	progress: UserBadgeProgress[];
}

interface BadgesState {
	/** Active badge catalogue (definitions). */
	catalogue: BadgeDefinition[];
	/** The current user's unlocked badges. */
	userBadges: UserBadge[];
	/** The current user's metric progress counters. */
	userProgress: UserBadgeProgress[];
	/** Metric registry entries for the admin badge form. */
	metricRegistry: MetricRegistryEntry[];

	catalogueRequest: RequestStatusObject;
	userBadgesRequest: RequestStatusObject;
	metricRegistryRequest: RequestStatusObject;
	saveBadgeRequest: RequestStatusObject;
	retireBadgeRequest: RequestStatusObject;
	markSeenRequest: RequestStatusObject;
}

const initialState: BadgesState = {
	catalogue: [],
	userBadges: [],
	userProgress: [],
	metricRegistry: [],

	catalogueRequest: createRequestStatusObject(),
	userBadgesRequest: createRequestStatusObject(),
	metricRegistryRequest: createRequestStatusObject(),
	saveBadgeRequest: createRequestStatusObject(),
	retireBadgeRequest: createRequestStatusObject(),
	markSeenRequest: createRequestStatusObject()
};

/** Fetch the active badge catalogue. */
export const listBadges = createAsyncThunk<
	BadgeDefinition[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('badges/listBadges', async () => {
	const response = await API.get('inspirusapi', '/badges', { headers: {} });
	return response.items ?? response;
});

/** Fetch the current user's unlocked badges and metric progress. */
export const listUserBadges = createAsyncThunk<
	UserBadgesResponse,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('badges/listUserBadges', async () => {
	const u = await getCachedAuthUser();
	const response = await API.get('inspirusapi', `/badges/users/${u.attributes.sub}`, {
		headers: {}
	});
	return {
		badges: response.badges ?? [],
		progress: response.progress ?? []
	};
});

/** Fetch the metric registry (admin badge form dropdown source). */
export const getMetricRegistry = createAsyncThunk<
	MetricRegistryEntry[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('badges/getMetricRegistry', async () => {
	const response = await API.get('inspirusapi', '/badges/metrics', { headers: {} });
	return response.items ?? response;
});

/** Create or update a badge definition (admin). */
export const saveBadge = createAsyncThunk<
	BadgeDefinition,
	{ badge: Partial<BadgeDefinition> & { id?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('badges/saveBadge', async ({ badge }) => {
	if (badge.id) {
		const response = await API.put('inspirusapi', `/badges/${badge.id}`, {
			headers: {},
			body: badge
		});
		return response;
	}
	const response = await API.post('inspirusapi', '/badges', {
		headers: {},
		body: badge
	});
	return response;
});

/** Retire (soft-delete) a badge definition (admin). */
export const retireBadge = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('badges/retireBadge', async ({ id }) => {
	await API.del('inspirusapi', `/badges/${id}`, { headers: {} });
	return id;
});

/**
 * Mark one or more freshly-unlocked badges as seen so their celebration is not
 * replayed on another device or a later session. Server-authoritative.
 */
export const markBadgesSeen = createAsyncThunk<
	string[],
	{ badgeIds: string[] },
	{ dispatch: AppDispatch; state: RootState }
>('badges/markBadgesSeen', async ({ badgeIds }) => {
	const u = await getCachedAuthUser();
	await API.post('inspirusapi', `/badges/users/${u.attributes.sub}/seen`, {
		headers: {},
		body: { badgeIds }
	});
	return badgeIds;
});

const badgesSlice = createSlice({
	name: 'badges',
	initialState,
	reducers: {
		resetBadges: () => initialState
	},
	extraReducers: (builder) => {
		buildReducers(builder, listBadges, 'catalogueRequest', {
			onFulfilled: (state, payload) => {
				state.catalogue = payload;
			}
		});
		buildReducers(builder, listUserBadges, 'userBadgesRequest', {
			onFulfilled: (state, payload) => {
				state.userBadges = payload.badges;
				state.userProgress = payload.progress;
			}
		});
		buildReducers(builder, getMetricRegistry, 'metricRegistryRequest', {
			onFulfilled: (state, payload) => {
				state.metricRegistry = payload;
			}
		});
		buildReducers(builder, saveBadge, 'saveBadgeRequest', {
			onFulfilled: (state, payload) => {
				const index = state.catalogue.findIndex((b: BadgeDefinition) => b.id === payload.id);
				if (index >= 0) {
					state.catalogue[index] = payload;
				} else {
					state.catalogue.push(payload);
				}
			}
		});
		buildReducers(builder, retireBadge, 'retireBadgeRequest', {
			onFulfilled: (state, payload) => {
				const index = state.catalogue.findIndex((b: BadgeDefinition) => b.id === payload);
				if (index >= 0) {
					state.catalogue[index] = { ...state.catalogue[index], active: false };
				}
			}
		});
		buildReducers(builder, markBadgesSeen, 'markSeenRequest', {
			onFulfilled: (state, payload) => {
				const seen = new Set(payload);
				state.userBadges = state.userBadges.map((b: UserBadge) =>
					seen.has(b.badgeId) ? { ...b, seenByUser: true } : b
				);
			}
		});
	}
});

export const { resetBadges } = badgesSlice.actions;
export default badgesSlice.reducer;
